import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import { canDM } from './contactService';
import { sendGroupInvitations } from './groupChatInvitationService';

/**
 * Chat Service - Firestore operations for chat system
 *
 * Handles:
 * - Chat CRUD operations
 * - Message sending/receiving
 * - Typing indicators
 * - Read receipts
 * - Chat membership
 *
 * CRITICAL RULE: Direct messages require users to be:
 * - Contacts (mutual acceptance), OR
 * - Members of the same organization
 *
 * No cold DMs allowed.
 */

// ============================================
// CHAT OPERATIONS
// ============================================

/**
 * Create a new direct message chat
 *
 * IMPORTANT: Must validate that users are contacts or org members
 * Enforces "no cold DMs" rule
 *
 * @param {string} currentUserId - Current user's ID
 * @param {string} otherUserId - Other user's ID
 * @param {string} currentUserName - Current user's display name
 * @param {string} otherUserName - Other user's display name
 * @param {string} currentUserPhoto - Current user's photo URL (optional)
 * @param {string} otherUserPhoto - Other user's photo URL (optional)
 * @throws Error if users are not authorized to DM
 */
export async function createDirectMessage(currentUserId, otherUserId, currentUserName, otherUserName, currentUserPhoto = null, otherUserPhoto = null) {
  // CRITICAL: Validate users can DM each other
  const authorized = await canDM(currentUserId, otherUserId);
  if (!authorized) {
    throw new Error('Cannot create DM: users are not contacts or in same organization');
  }

  // Check if DM already exists between these users
  const existingChat = await findExistingDM(currentUserId, otherUserId);
  if (existingChat) {
    return existingChat;
  }

  // Generate chat ID first (iOS expects 'id' field in document)
  const chatRef = doc(collection(db, 'chats'));
  const chatId = chatRef.id;

  // Create new DM chat with iOS-compatible structure
  const chatData = {
    id: chatId, // iOS expects this field
    type: 'dm',
    name: null, // iOS expects null for DMs (uses participantDetails instead)
    participants: [currentUserId, otherUserId],
    // iOS uses participantDetails for display names/photos
    participantDetails: {
      [currentUserId]: {
        displayName: currentUserName || 'Unknown',
        photoURL: currentUserPhoto || null
      },
      [otherUserId]: {
        displayName: otherUserName || 'Unknown',
        photoURL: otherUserPhoto || null
      }
    },
    meetingId: null,
    organizationId: null,
    channelSlug: null,
    ownerId: null,
    createdBy: currentUserId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessage: null,
    isActive: true
  };

  // Use batch write for atomicity - prevents race condition where one membership fails
  const batch = writeBatch(db);

  // Add chat document to batch
  batch.set(chatRef, chatData);

  // Add membership for both users in the same batch
  // Both users get 'active' status since they're already contacts (no approval needed)
  const currentUserMembershipRef = doc(db, `chatMembership/${currentUserId}/chats/${chatId}`);
  const otherUserMembershipRef = doc(db, `chatMembership/${otherUserId}/chats/${chatId}`);

  const membershipData = (userId) => ({
    chatId,
    chatType: 'dm',
    status: 'active',
    joinedAt: serverTimestamp(),
    lastReadAt: serverTimestamp(),
    muted: false,
    pinned: false,
    archived: false,
    organizationId: null
  });

  batch.set(currentUserMembershipRef, membershipData(currentUserId));
  batch.set(otherUserMembershipRef, membershipData(otherUserId));

  // Commit all writes atomically
  await batch.commit();

  return { id: chatId, ...chatData };
}

/**
 * Create a DM using the secure Cloud Function
 * This is the REQUIRED method for DM creation - Firestore rules block direct DM creation
 *
 * @param {string} currentUserId - Current user's ID
 * @param {string} otherUserId - Other user's ID
 * @param {string} currentUserName - Current user's display name
 * @param {string} otherUserName - Other user's display name
 * @param {string} currentUserPhoto - Current user's photo URL (optional)
 * @param {string} otherUserPhoto - Other user's photo URL (optional)
 * @returns {Promise<{id: string, existing: boolean}>}
 */
export async function createDirectMessageSecure(currentUserId, otherUserId, currentUserName, otherUserName, currentUserPhoto = null, otherUserPhoto = null) {
  // First check if DM already exists (avoid calling Cloud Function unnecessarily)
  const existingChat = await findExistingDM(currentUserId, otherUserId);
  if (existingChat) {
    return { id: existingChat.id, existing: true, ...existingChat };
  }

  // Call Cloud Function to create DM with server-side validation
  const functions = getFunctions();
  const createDMFn = httpsCallable(functions, 'createDirectMessageSecure');

  const result = await createDMFn({
    otherUserId,
    currentUserName,
    otherUserName,
    currentUserPhoto,
    otherUserPhoto
  });

  if (!result.data.success) {
    throw new Error(result.data.error || 'Failed to create DM');
  }

  // Fetch the created chat to return full data
  const chatDoc = await getDoc(doc(db, 'chats', result.data.chatId));
  return {
    id: result.data.chatId,
    existing: result.data.existing || false,
    ...chatDoc.data()
  };
}

/**
 * Find existing DM between two users
 */
export async function findExistingDM(userId1, userId2) {
  const chatsRef = collection(db, 'chats');
  const q = query(
    chatsRef,
    where('type', '==', 'dm'),
    where('participants', 'array-contains', userId1)
  );

  const snapshot = await getDocs(q);
  for (const doc of snapshot.docs) {
    const chat = doc.data();
    if (chat.participants.includes(userId2)) {
      return { id: doc.id, ...chat };
    }
  }
  return null;
}

/**
 * Create a meeting chat
 */
export async function createMeetingChat(meetingId, meetingName, creatorId) {
  const chatData = {
    type: 'meeting',
    name: meetingName,
    participants: [creatorId],
    meetingId: meetingId,
    organizationId: null,
    channelSlug: null,
    ownerId: null,
    createdBy: creatorId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessage: null,
    isActive: true
  };

  const chatRef = await addDoc(collection(db, 'chats'), chatData);
  await addChatMembership(creatorId, chatRef.id, 'meeting');

  return { id: chatRef.id, ...chatData };
}

/**
 * Create a group chat
 *
 * IMPORTANT: Now uses invitation system - only creator is added immediately,
 * other participants receive invitations they must accept
 */
export async function createGroupChat(name, creatorId, participantIds = [], creatorName = 'User') {
  // Only creator is added as initial participant
  const initialParticipants = [creatorId];

  const chatData = {
    type: 'group',
    name: name,
    participants: initialParticipants,
    meetingId: null,
    organizationId: null,
    channelSlug: null,
    ownerId: creatorId,
    createdBy: creatorId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessage: null,
    isActive: true
  };

  const chatRef = await addDoc(collection(db, 'chats'), chatData);

  // Add membership only for creator
  await addChatMembership(creatorId, chatRef.id, 'group');

  // Send invitations to other participants
  if (participantIds.length > 0) {
    const otherParticipants = participantIds.filter(id => id !== creatorId);
    if (otherParticipants.length > 0) {
      await sendGroupInvitations(
        chatRef.id,
        name,
        creatorId,
        creatorName,
        otherParticipants
      );
    }
  }

  // Add system message
  await sendSystemMessage(
    chatRef.id,
    `Chat "${name}" was created${participantIds.length > 0 ? `. Invitations sent to ${participantIds.length} ${participantIds.length === 1 ? 'person' : 'people'}.` : ''}`
  );

  return { id: chatRef.id, ...chatData };
}

/**
 * Create an organization channel
 */
export async function createOrgChannel(orgId, name, slug, description, creatorId, isDefault = false) {
  const chatData = {
    type: 'org_channel',
    name: name,
    participants: [], // Org channels use org membership
    meetingId: null,
    organizationId: orgId,
    channelSlug: slug,
    ownerId: null,
    createdBy: creatorId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessage: null,
    isActive: true
  };

  const chatRef = await addDoc(collection(db, 'chats'), chatData);

  // Create channel reference in org
  await setDoc(doc(db, `organizations/${orgId}/channels/${chatRef.id}`), {
    id: chatRef.id,
    name: name,
    slug: slug,
    description: description,
    chatId: chatRef.id,
    isDefault: isDefault,
    createdBy: creatorId,
    createdAt: serverTimestamp()
  });

  return { id: chatRef.id, ...chatData };
}

/**
 * Get a chat by ID
 */
export async function getChat(chatId) {
  const chatDoc = await getDoc(doc(db, 'chats', chatId));
  if (chatDoc.exists()) {
    return { id: chatDoc.id, ...chatDoc.data() };
  }
  return null;
}

/**
 * Archive a chat - LOCAL ONLY action (Apple-style UX)
 *
 * - Only affects the archiving user's view
 * - Does NOT notify or affect the other participant
 * - Chat auto-unarchives when a new message arrives
 * - No scheduled deletion, no social signaling
 */
export async function archiveChat(chatId, userId) {
  // Only update the user's membership - purely local action
  await updateDoc(doc(db, `chatMembership/${userId}/chats/${chatId}`), {
    archived: true,
    archivedAt: serverTimestamp()
  });
}

/**
 * Unarchive a chat - restore to main list
 */
export async function unarchiveChat(chatId, userId) {
  await updateDoc(doc(db, `chatMembership/${userId}/chats/${chatId}`), {
    archived: false,
    archivedAt: null
  });
}

/**
 * Delete a chat completely (removes for all participants)
 * Only use for DMs or if user is chat owner
 */
export async function deleteChat(chatId, userId) {
  // Get the chat to find all participants
  const chatDoc = await getDoc(doc(db, 'chats', chatId));
  if (!chatDoc.exists()) {
    throw new Error('Chat not found');
  }

  const chat = chatDoc.data();
  const participants = chat.participants || [];

  // Delete all memberships for participants
  const membershipDeletions = participants.map(participantId =>
    deleteDoc(doc(db, `chatMembership/${participantId}/chats/${chatId}`))
  );

  // Delete the chat document
  const chatDeletion = deleteDoc(doc(db, 'chats', chatId));

  await Promise.all([...membershipDeletions, chatDeletion]);
}

/**
 * Leave a chat (remove yourself from participants)
 * For group chats - removes your membership but keeps the chat
 */
export async function leaveChat(chatId, userId, userName) {
  const chatRef = doc(db, 'chats', chatId);
  const chatDoc = await getDoc(chatRef);

  if (!chatDoc.exists()) {
    throw new Error('Chat not found');
  }

  const chat = chatDoc.data();
  const participants = chat.participants.filter(id => id !== userId);

  // Update chat participants
  await updateDoc(chatRef, {
    participants,
    updatedAt: serverTimestamp()
  });

  // Remove membership
  await removeChatMembership(userId, chatId);

  // Add system message
  await sendSystemMessage(chatId, `${userName} left the chat`);
}

// ============================================
// MESSAGE OPERATIONS
// ============================================

/**
 * Send a text message
 */
export async function sendMessage(chatId, senderId, senderName, text, senderPhotoURL = null, isGuest = false) {
  const messageData = {
    senderId,
    senderName,
    senderPhotoURL,
    text,
    timestamp: serverTimestamp(),
    type: 'text',
    readBy: { [senderId]: Timestamp.now() },
    isGuest,
    attachment: null
  };

  const messageRef = await addDoc(collection(db, `chats/${chatId}/messages`), messageData);

  // Update chat's lastMessage
  await updateDoc(doc(db, 'chats', chatId), {
    lastMessage: {
      text: text.length > 50 ? text.substring(0, 50) + '...' : text,
      senderId,
      senderName,
      timestamp: serverTimestamp()
    },
    updatedAt: serverTimestamp()
  });

  return { id: messageRef.id, ...messageData };
}

/**
 * Send a meeting invite message with rich metadata
 */
export async function sendMeetingInviteMessage(chatId, senderId, senderName, senderPhotoURL = null, meetingData) {
  const { meetingId, meetingName, accessCode, personalNote } = meetingData;

  // Store a simple text fallback for notifications and previews
  const previewText = `${senderName} invited you to join: ${meetingName || `Meeting ${meetingId}`}`;

  const messageData = {
    senderId,
    senderName,
    senderPhotoURL,
    text: previewText,
    timestamp: serverTimestamp(),
    type: 'meeting-invite',
    readBy: { [senderId]: Timestamp.now() },
    isGuest: false,
    attachment: null,
    // Meeting invite specific data
    meetingInvite: {
      meetingId,
      meetingName: meetingName || `Meeting ${meetingId}`,
      accessCode: accessCode || null,
      personalNote: personalNote || null,
      shareLink: `https://www.quickmeet.cam/meet/${meetingId}`
    }
  };

  const messageRef = await addDoc(collection(db, `chats/${chatId}/messages`), messageData);

  // Update chat's lastMessage
  await updateDoc(doc(db, 'chats', chatId), {
    lastMessage: {
      text: previewText.length > 50 ? previewText.substring(0, 50) + '...' : previewText,
      senderId,
      senderName,
      timestamp: serverTimestamp()
    },
    updatedAt: serverTimestamp()
  });

  return { id: messageRef.id, ...messageData };
}

/**
 * Send a system message
 */
export async function sendSystemMessage(chatId, text) {
  const messageData = {
    senderId: 'system',
    senderName: 'System',
    senderPhotoURL: null,
    text,
    timestamp: serverTimestamp(),
    type: 'system',
    readBy: {},
    isGuest: false,
    attachment: null
  };

  await addDoc(collection(db, `chats/${chatId}/messages`), messageData);
}

/**
 * Send a call log message
 * Records call history in chat with duration and status
 *
 * @param {string} chatId - Chat to add message to
 * @param {object} callData - Call data object
 * @param {string} callData.callId - Call document ID
 * @param {string} callData.callerId - Caller's user ID
 * @param {string} callData.callerName - Caller's display name
 * @param {string} callData.calleeId - Callee's user ID
 * @param {string} callData.calleeName - Callee's display name
 * @param {string} callData.callType - 'video' or 'audio'
 * @param {string} callData.status - 'ended', 'declined', 'missed', 'busy', 'failed'
 * @param {number|null} callData.duration - Call duration in seconds (null if not answered)
 * @param {Date} callData.initiatedAt - When call was initiated
 * @param {Date} callData.endedAt - When call ended
 */
export async function sendCallLogMessage(chatId, callData) {
  const {
    callId,
    callerId,
    callerName,
    calleeId,
    calleeName,
    callType,
    status,
    duration,
    initiatedAt,
    endedAt
  } = callData;

  // Format duration string
  const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins === 0) return `${secs}s`;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Build preview text for chat list
  const callIcon = callType === 'video' ? '📹' : '📞';
  const callLabel = callType === 'video' ? 'Video call' : 'Audio call';
  let previewText;

  switch (status) {
    case 'ended':
      previewText = duration ? `${callIcon} ${callLabel} · ${formatDuration(duration)}` : `${callIcon} ${callLabel}`;
      break;
    case 'declined':
      previewText = `${callIcon} ${callLabel} declined`;
      break;
    case 'missed':
      previewText = `${callIcon} Missed ${callLabel.toLowerCase()}`;
      break;
    case 'busy':
      previewText = `${callIcon} ${callLabel} - busy`;
      break;
    case 'failed':
      previewText = `${callIcon} ${callLabel} failed`;
      break;
    default:
      previewText = `${callIcon} ${callLabel}`;
  }

  const messageData = {
    senderId: callerId,
    senderName: callerName || 'Unknown',
    senderPhotoURL: null,
    text: previewText,
    timestamp: serverTimestamp(),
    type: 'call',
    readBy: { [callerId]: Timestamp.now() },
    isGuest: false,
    attachment: null,
    callData: {
      callId,
      callerId,
      callerName: callerName || 'Unknown',
      calleeId,
      calleeName: calleeName || 'Unknown',
      callType,
      status,
      duration: duration || null,
      initiatedAt: initiatedAt ? Timestamp.fromDate(new Date(initiatedAt)) : null,
      endedAt: endedAt ? Timestamp.fromDate(new Date(endedAt)) : serverTimestamp()
    }
  };

  const messageRef = await addDoc(collection(db, `chats/${chatId}/messages`), messageData);

  // Update chat's lastMessage
  await updateDoc(doc(db, 'chats', chatId), {
    lastMessage: {
      text: previewText,
      senderId: callerId,
      senderName: callerName || 'Unknown',
      timestamp: serverTimestamp()
    },
    updatedAt: serverTimestamp()
  });

  return { id: messageRef.id, ...messageData };
}

/**
 * Send a file message (Pro/Team only)
 */
export async function sendFileMessage(chatId, senderId, senderName, attachment, senderPhotoURL = null) {
  const messageData = {
    senderId,
    senderName,
    senderPhotoURL,
    text: `Shared a file: ${attachment.name}`,
    timestamp: serverTimestamp(),
    type: 'file',
    readBy: { [senderId]: Timestamp.now() },
    isGuest: false,
    attachment
  };

  const messageRef = await addDoc(collection(db, `chats/${chatId}/messages`), messageData);

  // Update chat's lastMessage
  await updateDoc(doc(db, 'chats', chatId), {
    lastMessage: {
      text: `Shared a file: ${attachment.name}`,
      senderId,
      senderName,
      timestamp: serverTimestamp()
    },
    updatedAt: serverTimestamp()
  });

  return { id: messageRef.id, ...messageData };
}

/**
 * Subscribe to messages in a chat
 */
export function subscribeToMessages(chatId, callback, messageLimit = 100) {
  const messagesRef = collection(db, `chats/${chatId}/messages`);
  const q = query(messagesRef, orderBy('timestamp', 'asc'), limit(messageLimit));

  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate?.() || new Date()
    }));
    callback(messages);
  }, (error) => {
    console.error('Error subscribing to messages:', error);
    callback([]);
  });
}

/**
 * Mark messages as read
 * Uses batch write for atomic operation to prevent race conditions
 */
export async function markMessagesAsRead(chatId, userId, messageIds) {
  if (!messageIds || messageIds.length === 0) return;

  // Use batch write for atomic update
  const batch = writeBatch(db);
  const now = Timestamp.now();

  // Add all message updates to batch
  for (const messageId of messageIds) {
    const messageRef = doc(db, `chats/${chatId}/messages/${messageId}`);
    batch.update(messageRef, {
      [`readBy.${userId}`]: now
    });
  }

  // Add membership lastReadAt update to batch (iOS uses lastReadAt)
  const membershipRef = doc(db, `chatMembership/${userId}/chats/${chatId}`);
  batch.set(membershipRef, { lastReadAt: serverTimestamp() }, { merge: true });

  // Commit all updates atomically
  try {
    await batch.commit();
  } catch (error) {
    console.error('Error marking messages as read:', error);
    // If batch fails, try individual updates as fallback
    for (const messageId of messageIds) {
      try {
        await updateDoc(doc(db, `chats/${chatId}/messages/${messageId}`), {
          [`readBy.${userId}`]: now
        });
      } catch (e) {
        // Individual message update failed, continue with others
      }
    }
    await setDoc(membershipRef, { lastReadAt: serverTimestamp() }, { merge: true });
  }
}

// ============================================
// TYPING INDICATORS
// ============================================

/**
 * Set typing status
 */
export async function setTypingStatus(chatId, userId, userName, isTyping) {
  const typingRef = doc(db, `chats/${chatId}/typing/${userId}`);

  if (isTyping) {
    await setDoc(typingRef, {
      userId,
      userName,
      timestamp: serverTimestamp()
    });
  } else {
    try {
      await deleteDoc(typingRef);
    } catch (error) {
      // Ignore if doc doesn't exist
    }
  }
}

/**
 * Subscribe to typing indicators
 */
export function subscribeToTyping(chatId, currentUserId, callback) {
  const typingRef = collection(db, `chats/${chatId}/typing`);

  return onSnapshot(typingRef, (snapshot) => {
    const typingUsers = snapshot.docs
      .map(doc => doc.data())
      .filter(data => data.userId !== currentUserId)
      .filter(data => {
        // Filter out expired typing (> 5 seconds old)
        const timestamp = data.timestamp?.toDate?.();
        if (!timestamp) return false;
        return (Date.now() - timestamp.getTime()) < 5000;
      });
    callback(typingUsers);
  });
}

// ============================================
// CHAT MEMBERSHIP
// ============================================

/**
 * Add chat membership for a user
 *
 * iOS-compatible structure:
 * - chatId: string
 * - chatType: 'dm' | 'group' | 'meeting' | 'org_channel'
 * - status: 'active' | 'pending'
 * - joinedAt: Timestamp
 * - lastReadAt: Timestamp | null
 * - muted: boolean
 * - pinned: boolean
 * - archived: boolean
 * - organizationId: string | null
 *
 * @param {string} userId - User ID
 * @param {string} chatId - Chat ID
 * @param {string} chatType - Type of chat (dm, group, meeting, org_channel)
 * @param {string|null} organizationId - Organization ID (if applicable)
 * @param {string} status - Membership status: 'active' | 'pending' (default: 'active')
 */
export async function addChatMembership(userId, chatId, chatType, organizationId = null, status = 'active') {
  await setDoc(doc(db, `chatMembership/${userId}/chats/${chatId}`), {
    chatId,
    chatType,
    status,
    joinedAt: serverTimestamp(),
    lastReadAt: status === 'active' ? serverTimestamp() : null, // pending users have null lastReadAt
    muted: false,
    pinned: false,
    archived: false, // iOS expects this field
    organizationId
  });
}

/**
 * Remove chat membership
 */
export async function removeChatMembership(userId, chatId) {
  await deleteDoc(doc(db, `chatMembership/${userId}/chats/${chatId}`));
}

/**
 * Update membership settings (mute, pin)
 */
export async function updateMembershipSettings(userId, chatId, settings) {
  await updateDoc(doc(db, `chatMembership/${userId}/chats/${chatId}`), settings);
}

/**
 * Accept a pending chat request
 * Changes membership status from 'pending' to 'active'
 */
export async function acceptChatRequest(userId, chatId) {
  await updateDoc(doc(db, `chatMembership/${userId}/chats/${chatId}`), {
    status: 'active',
    lastReadAt: serverTimestamp()
  });
}

/**
 * Decline a pending chat request
 * Deletes the chat and all memberships for both users
 * Uses batch write for atomic operation to prevent orphaned data
 */
export async function declineChatRequest(userId, chatId) {
  // Get the chat to find all participants
  const chatDoc = await getDoc(doc(db, 'chats', chatId));
  if (!chatDoc.exists()) {
    throw new Error('Chat not found');
  }

  const chat = chatDoc.data();
  const participants = chat.participants || [];

  // Use batch write for atomic deletion
  const batch = writeBatch(db);

  // Add all membership deletions to batch
  for (const participantId of participants) {
    const membershipRef = doc(db, `chatMembership/${participantId}/chats/${chatId}`);
    batch.delete(membershipRef);
  }

  // Add chat deletion to batch
  batch.delete(doc(db, 'chats', chatId));

  // Commit all deletions atomically
  await batch.commit();
}

// ============================================
// GROUP CHAT OPERATIONS
// ============================================

/**
 * Add participant to group chat
 */
export async function addGroupParticipant(chatId, userId, addedByName) {
  const chatRef = doc(db, 'chats', chatId);
  const chatDoc = await getDoc(chatRef);

  if (!chatDoc.exists() || chatDoc.data().type !== 'group') {
    throw new Error('Chat not found or not a group chat');
  }

  const participants = chatDoc.data().participants || [];
  if (participants.includes(userId)) {
    throw new Error('User is already a participant');
  }

  await updateDoc(chatRef, {
    participants: [...participants, userId],
    updatedAt: serverTimestamp()
  });

  await addChatMembership(userId, chatId, 'group');
  await sendSystemMessage(chatId, `${addedByName} added a new member`);
}

/**
 * Remove participant from group chat
 */
export async function removeGroupParticipant(chatId, userId, removedByName, isLeaving = false) {
  const chatRef = doc(db, 'chats', chatId);
  const chatDoc = await getDoc(chatRef);

  if (!chatDoc.exists() || chatDoc.data().type !== 'group') {
    throw new Error('Chat not found or not a group chat');
  }

  const participants = chatDoc.data().participants.filter(id => id !== userId);

  await updateDoc(chatRef, {
    participants,
    updatedAt: serverTimestamp()
  });

  await removeChatMembership(userId, chatId);

  if (isLeaving) {
    await sendSystemMessage(chatId, `A member left the chat`);
  } else {
    await sendSystemMessage(chatId, `${removedByName} removed a member`);
  }
}

/**
 * Rename group chat
 */
export async function renameGroupChat(chatId, newName, renamedByName) {
  await updateDoc(doc(db, 'chats', chatId), {
    name: newName,
    updatedAt: serverTimestamp()
  });

  await sendSystemMessage(chatId, `${renamedByName} renamed the chat to "${newName}"`);
}

// ============================================
// MEETING CHAT OPERATIONS
// ============================================

/**
 * Add participant to meeting chat
 */
export async function addMeetingParticipant(chatId, participantId, isGuest = false) {
  const chatRef = doc(db, 'chats', chatId);
  const chatDoc = await getDoc(chatRef);

  if (!chatDoc.exists()) return;

  const participants = chatDoc.data().participants || [];
  if (!participants.includes(participantId)) {
    await updateDoc(chatRef, {
      participants: [...participants, participantId]
    });
  }

  if (!isGuest) {
    await addChatMembership(participantId, chatId, 'meeting');
  }
}

/**
 * End meeting chat (revoke guest access)
 */
export async function endMeetingChat(chatId) {
  await updateDoc(doc(db, 'chats', chatId), {
    meetingEndedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  await sendSystemMessage(chatId, 'Meeting has ended');
}

// ============================================
// ORG CHANNEL OPERATIONS
// ============================================

/**
 * Get organization channels
 */
export async function getOrgChannels(orgId) {
  const channelsRef = collection(db, `organizations/${orgId}/channels`);
  const snapshot = await getDocs(channelsRef);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * Create default org channels
 */
export async function createDefaultOrgChannels(orgId, creatorId) {
  const defaultChannels = [
    { name: 'General', slug: 'general', description: 'General discussion', isDefault: true },
    { name: 'Meetings', slug: 'meetings', description: 'Meeting-related discussions', isDefault: true }
  ];

  for (const channel of defaultChannels) {
    await createOrgChannel(
      orgId,
      channel.name,
      channel.slug,
      channel.description,
      creatorId,
      channel.isDefault
    );
  }
}

// ============================================
// MESSAGE CLEANUP
// ============================================

/**
 * Delete old messages from a chat (for free tier auto-cleanup)
 * Deletes messages older than specified days
 */
export async function cleanupOldMessages(chatId, maxDays = 7) {
  const messagesRef = collection(db, `chats/${chatId}/messages`);
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - maxDays);
  const cutoffTimestamp = Timestamp.fromDate(cutoffDate);

  try {
    const q = query(
      messagesRef,
      where('timestamp', '<', cutoffTimestamp),
      orderBy('timestamp', 'asc'),
      limit(100) // Process in batches of 100
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return 0;
    }

    const batch = writeBatch(db);
    let deletedCount = 0;

    snapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
      deletedCount++;
    });

    await batch.commit();
    return deletedCount;
  } catch (error) {
    console.error('Error cleaning up old messages:', error);
    return 0;
  }
}

/**
 * Cleanup all old messages for free tier user across all chats
 */
export async function cleanupUserChatHistory(userId, tier = 'free') {
  const tierLimits = {
    free: 7,
    pro: 90,
    team: -1 // unlimited
  };

  const maxDays = tierLimits[tier] || 7;

  // Don't cleanup for unlimited tiers
  if (maxDays === -1) {
    return 0;
  }

  try {
    const membershipRef = collection(db, `chatMembership/${userId}/chats`);
    const snapshot = await getDocs(membershipRef);

    let totalDeleted = 0;

    for (const doc of snapshot.docs) {
      const chatId = doc.data().chatId;
      const deleted = await cleanupOldMessages(chatId, maxDays);
      totalDeleted += deleted;
    }

    return totalDeleted;
  } catch (error) {
    console.error('Error cleaning up user chat history:', error);
    return 0;
  }
}

export default {
  // Chat operations
  createDirectMessage,
  createMeetingChat,
  createGroupChat,
  createOrgChannel,
  getChat,
  archiveChat,
  unarchiveChat,
  deleteChat,
  leaveChat,

  // Message operations
  sendMessage,
  sendSystemMessage,
  sendFileMessage,
  subscribeToMessages,
  markMessagesAsRead,

  // Typing
  setTypingStatus,
  subscribeToTyping,

  // Membership
  addChatMembership,
  removeChatMembership,
  updateMembershipSettings,
  acceptChatRequest,
  declineChatRequest,

  // Group operations
  addGroupParticipant,
  removeGroupParticipant,
  renameGroupChat,

  // Meeting operations
  addMeetingParticipant,
  endMeetingChat,

  // Org channels
  getOrgChannels,
  createDefaultOrgChannels,

  // Cleanup
  cleanupOldMessages,
  cleanupUserChatHistory
};
